import React, { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/actions/action";
import { useSelectUserQuery, useUpdateUserMutation, useExecuteSummaryQuery } from "src/api/userAPI";
import { useSkuRateQuery } from "src/api/skuAPI";
import { getCookie, logoutCookie } from "src/utils/cookieUtil";
import { getPathValue } from "src/utils/queryStringUtil";
import { shallowEqual } from "react-redux";
import { useDispatch } from "react-redux";
import { setToggleData } from "src/store/reducers/toggleReduce";
import Body from "src/components/common/Body";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import SVG from "src/utils/SVG";
import IconLink from "src/assets/images/iconLink.svg";
import IconLinkGrey from "src/assets/images/iconLinkGrey.svg";
import Modal, { RefProps } from "src/components/common/Modal";
import styled from "styled-components";
import KakaoMap from "src/components/common/KakaoMap";
import { useLogoutQuery } from "src/api/loginAPI";
import cn from "classnames";
import moment from "moment";
import IcoCate from "src/assets/images/icoCate.svg";
import IcoTel from "src/assets/images/icoTel.svg";
import IcoMap from "src/assets/images/icoMap.svg";
import IcoDate from "src/assets/images/icoDate.svg";
import IcoUser from "src/assets/images/icoUser.svg";
import IcoEmail from "src/assets/images/icoEmail.svg";
import IcoAsed from "src/assets/images/icoAsed.svg";
import IcoNotice from "src/assets/images/icoNotice.svg";
import IcoSku from "src/assets/images/icoSku.svg";
import ErrorParser from "src/utils/jsonParser";
import TextInput from "src/components/common/TextInput";
import Select from "src/components/common/Select";
import { UserVo, ImageData } from "src/vo/userVo";
import { contractTypeName, isChannelOutlet, shopTypeName } from "src/constants/storeAxis";
import { isEmpty } from "lodash";
import SkuList from "src/components/profile/SkuList";
import NoticeFulfillList from "src/components/profile/NoticeFulfillList";
import ImageSlider from "src/components/profile/ImageSlider";
import StoreImageUpload from "src/components/common/StoreImageUpload";

const brandList: { value: string; name: string }[] = [
  { value: "CB", name: "전체브랜드" },
  { value: "M", name: "MLB" },
  { value: "I", name: "MLB KIDS" },
  { value: "X", name: "Discovery" },
  { value: "A", name: "STRETCH ANGELS" },
  { value: "V", name: "DUVETICA" },
  { value: "ST", name: "Sergio Tacchini" },
];

/**
 * 상세에 보여 줄 매장 구분 한 줄 (VMD-403).
 *
 * 계약형태와 매장형태를 가운뎃점으로 이어 붙이고, 상설이면 뒤에 덧붙인다.
 * 두 축이 아직 비어 있으면(운영 DDL 적용 직후, 크론이 아직 돌지 않은 상태)
 * 기존 store_type 라벨로 되돌아간다.
 */
const storeAxisText = (user: UserVo) => {
  const parts = [contractTypeName(user.contract_type_cd), shopTypeName(user.analytic_store_type)].filter(Boolean);

  // 유통채널 07 인데 매장형태가 L 이 아닌 매장은 매장형태만 보면 아울렛인 것을
  // 알 수 없다. 매장형태 라벨을 덮어쓰지 않고 뒤에 덧붙인다 — 등록된 매장형태는
  // 실제로 백화점이고 아울렛인 근거는 유통채널이라는 다른 축이다.
  if (isChannelOutlet({ analytic_store_type: user.analytic_store_type, store_type: user.store_type })) {
    parts.push("아울렛");
  }
  if (user.is_permanent) {
    parts.push("상설");
  }
  return parts.join(" · ");
};

const getBrandDisplayName = (code?: string, name?: string) => {
  if (name) return name;
  if (!code) return "";
  return brandList.find((b) => b.value === code)?.name ?? code;
};

const example = [
  {
    value: "인테리어",
    name: "인테리어",
  },
  {
    value: "전기",
    name: "전기",
  },
  {
    value: "금속",
    name: "금속",
  },
  {
    value: "로고",
    name: "로고",
  },
  {
    value: "목공",
    name: "목공",
  },
  {
    value: "타일",
    name: "타일",
  },
  {
    value: "영상",
    name: "영상",
  },
  {
    value: "모니터",
    name: "모니터",
  },
  {
    value: "스피커",
    name: "스피커",
  },
];

function profile() {
  // 라이브러리 선언
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeId: any = getPathValue();

  // 초기 값 세팅
  const userInfo = useAppSelector((state) => state.userReduce.userInfo, shallowEqual);
  // const [userProfile, setUserProfile] = useState(userInfo);
  const [userProfile, setUserProfile] = useState<UserVo>({
    address: "",
  });
  const storeAxisLabel = storeAxisText(userProfile);
  const [apiSkip, setApiSkip] = useState(true);
  const [isMind, setIsMind] = useState(storeId === userInfo.user_id ? true : false);

  const [callMap, setCallMap] = useState<boolean>(true);


  const [inputs, setInputs] = useState({
    companyType: "",
    mChargeName: "",
    mPhone: "",
    mAddress: "",
    sPhone: "",
    sAddress: "",
    sChargeName: "",
    sOpenDate: "",
    eBrand: "",
  });
  const { companyType, eBrand, mAddress, mChargeName, mPhone, sAddress, sChargeName, sOpenDate, sPhone } = inputs;

  const [isUpdateCheck, setIsUpdateCheck] = useState<boolean>(false);
  
  // 이미지 관리 상태
  const [storeImages, setStoreImages] = useState<(File | ImageData)[]>([]);
  
  // 삭제된 이미지 ID 추적
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);

  // 이미지 삭제 시 호출되는 콜백
  const handleDeleteImage = (imageId: number) => {
    setDeletedImageIds(prev => [...prev, imageId]);
  };

  // API 영역
  const storeInfo = useSelectUserQuery(storeId, { skip: storeId === undefined });
  const logout = useLogoutQuery(getCookie("re_token"), { skip: apiSkip });
  const executeSum = useExecuteSummaryQuery({ user_id: storeId }, { skip: userInfo.user_type === "E" && userInfo.user_id === storeId });
  const skuInfo = useSkuRateQuery(
    { shop_code: userProfile.erp_code, brand_code: userProfile.brand },
    {
      skip: userInfo.user_type === "E" && userInfo.user_id === storeId,
    },
  );

  const [updateUser, { isError, isSuccess, isLoading, error }] = useUpdateUserMutation();

  // Hook 영역
  useLayoutEffect(() => {
    if (storeId !== undefined && storeInfo.isSuccess) {
      // 수정
      // if (userInfo.user_type === "E" && (storeInfo.data.data.user_type === "M" || storeInfo.data.data.user_type === "S")) {
      //   setIsMind(false);
      // } else {
      //   setIsMind(true);
      // }
      if (userInfo.user_id === storeInfo.data.data.user_id) {
        setIsMind(true);
      } else {
        setIsMind(false);
      }
      setUserProfile(storeInfo.data.data);
      setInputs({
        ...inputs,
        mAddress: storeInfo.data.data.address,
        mPhone: storeInfo.data.data.phone_number,
        mChargeName: storeInfo.data.data.manager,
        companyType: storeInfo.data.data.company_type,
        sAddress: storeInfo.data.data.address,
        sChargeName: storeInfo.data.data.manager,
        sPhone: storeInfo.data.data.phone_number,
        eBrand: storeInfo.data.data.brand ?? "",
      });
      
      // 기존 이미지 설정
      if (storeInfo.data.data.images) {
        setStoreImages(storeInfo.data.data.images);
      }
    }
  }, [storeInfo]);

  // useEffect(() => {
  //   console.log("===", storeId, "===", userInfo.user_id);
  //   setIsMind(storeId === userInfo.user_id);
  // }, [userProfile]);
  useEffect(() => {
    if (storeId === undefined) {
      // setIsMind(false);
      setUserProfile(userInfo);
    }
  }, []);

  useEffect(() => {
    if (logout.isSuccess) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage("");
      }
      logoutCookie();
      window.sessionStorage.clear();
      const timer = setTimeout(() => {
        navigate("/");
      }, 200);
      return () => clearTimeout(timer);
    }
    if (logout.isError) {
      const message = ErrorParser(logout.error);
      alert(message);
      // logoutCookie();
    }
  }, [logout]);

  useEffect(() => {
    if (isSuccess) {
      showCopyToast("수정되었습니다.");
      storeInfo.refetch();
      // setCallMap(true);
    }
    if (isError) {
      const message = ErrorParser(error);
      showCopyToast(message, "ERROR");
    }
  }, [isSuccess, isError]);

  // 로그아웃 : 모달 관련
  const logoutModalRef = React.useRef<RefProps>(null);
  const logoutBtn = () => {
    if (logoutModalRef.current) logoutModalRef.current.modalOpen();
  };

  const cancelLogout = () => {
    if (logoutModalRef.current) logoutModalRef.current.modalClose();
  };

  const confirmLogout = () => {
    setApiSkip(false);
  };

  // 복사 알림 토스트 관련
  const showCopyToast = (message: string, mode: "SUCCESS" | "ERROR" = "SUCCESS") => {
    dispatch(setToggleData({ mode, data: message }));
  };

  // 헤더 스크롤
  const [isScrollTop, setIsScrollTop] = useState(true);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll); //clean up
    };
  }, [isScrollTop]);

  const handleScroll = useCallback(() => {
    if (window.scrollY >= 50) {
      setIsScrollTop(false);
      return;
    } else {
      setIsScrollTop(true);
      return;
    }
  }, [isScrollTop]);

  const profileUpdate = async () => {
    // 정보 수정 쿼리 필요
    const targetUserType = userInfo.user_type === "E" ? userProfile.user_type : userInfo.user_type;
    
    if (targetUserType === "M") {
      const params = {
        address: mAddress,
        phone_number: mPhone,
        company_type: isEmpty(companyType) ? "인테리어" : companyType,
        manager: mChargeName,
      };
      setCallMap(true);
      await updateUser({ userId: storeId, data: params });
    } else if (targetUserType === "S") {
      
      // FormData 생성 (이미지 파일 포함)
      const formData = new FormData();
      
      // 기존 데이터 추가
      const userData = {
        address: sAddress,
        phone_number: sPhone,
        manager: sChargeName,
      };
      
      formData.append('data', JSON.stringify(userData));
      
      // 이미지 파일들 처리
      const newFiles: File[] = [];
      const existingImages: ImageData[] = [];
      
      storeImages.forEach((image) => {
        if (image instanceof File) {
          // 새로운 파일
          formData.append(`images`, image as File);
          newFiles.push(image as File);
        } else {
          // 기존 이미지
          existingImages.push(image as ImageData);
        }
      });
      
      // 기존 이미지 정보도 전달
      if (existingImages.length > 0) {
        formData.append('existing_images', JSON.stringify(existingImages));
      }
      
      // 삭제된 이미지 ID 전달
      if (deletedImageIds.length > 0) {
        deletedImageIds.forEach(id => {
          formData.append('delete_images[]', id.toString());
        });
      }
      
      setCallMap(true);
      
      await updateUser({ userId: storeId, data: formData });
      // 성공 시 삭제된 이미지 ID 배열 초기화
      setDeletedImageIds([]);
    } else if (targetUserType === "E") {
      await updateUser({ userId: storeId, data: { brand: eBrand } });
    }
  };

  const onChange = (e: any) => {
    const { value, id } = e.target;
    if (id === "sPhone" || id === "mPhone") {
      const onlyNumber = value.replace(/[^0-9]/g, "");
      setInputs({
        ...inputs,
        [id]: onlyNumber,
      });
    } else {
      setInputs({
        ...inputs,
        [id]: value,
      });
    }
  };

  return (
    <>
      <header className={cn(isScrollTop ? "sub-top" : "")}>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            프로필
          </Button>
        </div>
        {isMind && (
          <div className="btn-box">
            <IconButton btnName="로그아웃" className="logout" onClick={logoutBtn}>
              <SVG name="logout" />
            </IconButton>
          </div>
        )}
      </header>

      <Body type="profile">
            {userProfile && userProfile.user_type !== "E" ? (
              <>
                {/* 이미지가 있으면 이미지 슬라이더 표시 */}
                {userProfile.images && userProfile.images.length > 0 ? (
                  <ImageSliderContainer>
                    <ImageSlider 
                      images={userProfile.images}
                    />
                  </ImageSliderContainer>
                ) : (
                  /* 이미지가 없고 주소가 있으면 지도 표시 */
                  !isEmpty(userProfile.address) ? (
                    <MapContainer>
                      <KakaoMap 
                        address={String(userProfile.address)} 
                        callMap={callMap}
                      />
                    </MapContainer>
                  ) : (
                    /* 이미지도 주소도 없으면 NoMap 표시 */
                    <NoMap></NoMap>
                  )
                )}
              </>
            ) : (
              <>
                <NoMap></NoMap>
              </>
            )}
        {/* 매장, 업체인 경우 storeInfo, userInfo 세팅 후 렌더링 */}
        {((isMind && storeInfo.isSuccess) || !isMind) && (
          <>
            <TitleBox>
              <div>
                <p className="name">
                  {userProfile.name}
                  <span>{userProfile.user_id}</span>
                </p>
                {/* 매장 사용자 본인이거나, 관리자가 매장 프로필을 볼 때, 또는 임직원이 임직원 프로필을 볼 때 수정 버튼 표시 */}
                {((userInfo.user_type === "S" && userInfo.user_id === userProfile.user_id) ||
                  (userInfo.user_type === "E" && userProfile.user_type === "S") ||
                  (userInfo.user_type === "E" && userProfile.user_type === "E")) ? (
                  <>
                    {isUpdateCheck ? (
                      <div>
                        <button type="button" className="edit-btn cancel" onClick={() => setIsUpdateCheck(false)}>
                          취소
                        </button>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => {
                            setIsUpdateCheck(false), profileUpdate();
                          }}
                        >
                          저장
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => {
                          setIsUpdateCheck(true), setCallMap(false);
                        }}
                      >
                        수정
                      </button>
                    )}
                  </>
                ) : (
                  /* 업체 사용자 본인일 때만 수정 버튼 표시 */
                  userInfo.user_type === "M" && userInfo.user_id === userProfile.user_id ? (
                    <>
                      {isUpdateCheck ? (
                        <div>
                          <button type="button" className="edit-btn cancel" onClick={() => setIsUpdateCheck(false)}>
                            취소
                          </button>
                          <button
                            type="button"
                            className="edit-btn"
                            onClick={() => {
                              setIsUpdateCheck(false), profileUpdate();
                            }}
                          >
                            저장
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => {
                            setIsUpdateCheck(true), setCallMap(false);
                          }}
                        >
                          수정
                        </button>
                      )}
                    </>
                  ) : ""
                )}
              </div>
            </TitleBox>

            {!isUpdateCheck && (
              <ProfileBox>
                {userProfile.company_type && <p className="ico-info category">{userProfile.company_type}</p>}

                {/*
                  매장 구분을 계약형태와 매장형태 두 값으로 함께 보여 준다 (VMD-403).
                  상세에는 지금까지 매장 구분이 아예 없었다. 칩에서 접은 홀세일, 기타,
                  온라인도 여기서는 등록 라벨 그대로 보여 준다 — 칩에서 접는 것과 값을
                  숨기는 것은 다르다.
                */}
                {storeAxisLabel && <p className="ico-info category">{storeAxisLabel}</p>}

                {(userProfile.brand_name || userProfile.brand) && (
                  <p className="ico-info brand">{getBrandDisplayName(userProfile.brand, userProfile.brand_name)}</p>
                )}

                {(userProfile.email || userProfile.address) && (
                  <>
                    {userProfile.user_type === "E" ? (
                      <p className="ico-info address email">{userProfile.email}</p>
                    ) : (
                      <p className="ico-info address map">
                        {userProfile.address}
                        <button
                          type="button"
                          aria-label="주소를 클립보드에 복사"
                          onClick={() => {
                            if (userProfile.address) {
                              navigator.clipboard.writeText(userProfile.address).then(() => {
                                showCopyToast("주소가 복사되었습니다.");
                              }).catch(() => {
                                showCopyToast("주소 복사에 실패했습니다.", "ERROR");
                              });
                            }
                          }}
                        >
                          복사
                        </button>
                        <button
                          type="button"
                          aria-label="지도보기"
                          onClick={() => {
                            navigate(`/map/${userProfile.user_id}`, { 
                              state: { 
                                address: userProfile.address,
                                storeName: userProfile.name 
                              } 
                            });
                          }}
                        >
                          지도보기
                        </button>
                      </p>
                    )}
                  </>
                )}

                {/* 담당자 */}
                {userProfile.manager && <p className="ico-info person">{userProfile.manager}</p>}

                {userProfile.phone_number && (
                  <p className="ico-info tel">
                    {userProfile.phone_number}
                    <a href={`tel:${userProfile.phone_number}`}>통화</a>
                  </p>
                )}

                {userProfile.user_type === "S" && userProfile.open_date ? (
                  <p className="ico-info info">오픈 {moment(userProfile.open_date).format("YYYY.MM.DD")}</p>
                ) : (
                  ""
                )}

                {/* A/S 이력 : 본사가 매장 프로필 볼때 O, 매장이 본인 매장 프로필 볼때 O */}
                {(userInfo.user_type === "E" && userProfile.user_type === "S") || (userInfo.user_type === "S" && userInfo.user_id === userProfile.user_id) ? (
                  <Link to={"/ased-detail/" + userProfile.user_id}>
                    <p className="ico-info as">
                      <span>A/S 이력</span>
                    </p>
                  </Link>
                ) : (
                  ""
                )}

                {/* 공지이행 이력 : 본사 O, 매장 O, 업체 O */}
                {userProfile.user_type === "S" && (
                  <Link to={"/fulfilled-detail/" + userProfile.user_id} state={executeSum.data}>
                    <p className="ico-info notice">
                      <span>공지이행 이력</span>
                    </p>
                    <NoticeFulfillList data={executeSum.data} />
                  </Link>
                )}

                {/* sku : 본사가 매장 프로필 볼때 O */}
                {userInfo.user_type === "E" &&
                  userProfile.user_type === "S" &&
                  skuInfo.isSuccess &&
                  (skuInfo.data.data.constructor !== Object ? (
                    ""
                  ) : (
                    <Link to={"/sku-detail/" + userProfile.user_id}>
                      <p className="ico-info sku">
                        <span>SKU</span>
                      </p>
                      <SkuList skuData={skuInfo.data.data} />
                    </Link>
                  ))}
              </ProfileBox>
            )}
          </>
        )}

        {isUpdateCheck && (
          <ProfileEditBox>
            {/* 업체 노출 */}
            {(userInfo.user_type === "M" || (userInfo.user_type === "E" && userProfile.user_type === "M")) && (
              <dl>
                <div>
                  <dt>구분</dt>
                  <dd>
                    <Select size="small" options={example} defaultValue={inputs.companyType} id="companyType" onChange={(e) => onChange(e)} />
                  </dd>
                </div>
                <div>
                  <dt>주소</dt>
                  <dd>
                    <TextInput
                      variant="contained"
                      type="text"
                      value={inputs.mAddress}
                      id="mAddress"
                      name="mProfile01"
                      onChange={(e) => onChange(e)}
                      maxLength={50}
                    />
                  </dd>
                </div>
                <div>
                  <dt>담당</dt>
                  <dd className="short">
                    <TextInput
                      variant="contained"
                      type="text"
                      value={inputs.mChargeName}
                      id="mChargeName"
                      name="mProfile02"
                      onChange={(e) => onChange(e)}
                      maxLength={10}
                    />
                  </dd>
                </div>
                <div>
                  <dt>번호</dt>
                  <dd className="short">
                    <TextInput
                      variant="contained"
                      type="text"
                      pattern="\d*"
                      value={inputs.mPhone}
                      id="mPhone"
                      name="mProfile03"
                      onChange={(e) => onChange(e)}
                      maxLength={11}
                      placeholder="'-' 제외 숫자만 작성"
                    />
                  </dd>
                </div>
              </dl>
            )}

            {/* 매장 노출 */}
            {(userInfo.user_type === "S" || (userInfo.user_type === "E" && userProfile.user_type === "S")) && (
              <dl>
                <div>
                  <dt>매장 이미지</dt>
                  <dd>
                    <StoreImageSection>
                      <StoreImageUpload
                        imageList={storeImages}
                        setImageList={setStoreImages}
                        maxImages={3}
                        onDeleteImage={handleDeleteImage}
                      />
                      <ImageHelpText>
                        • 최대 3개의 매장 이미지를 업로드할 수 있습니다.<br />
                        • 이미지 크기: 최대 10MB<br />
                        • 지원 형식: JPG, PNG
                      </ImageHelpText>
                    </StoreImageSection>
                  </dd>
                </div>
                <div>
                  <dt>주소</dt>
                  <dd>
                    <TextInput
                      variant="contained"
                      type="text"
                      value={inputs.sAddress}
                      id="sAddress"
                      name="sProfile01"
                      onChange={(e) => onChange(e)}
                      maxLength={50}
                    />
                  </dd>
                </div>
                <div>
                  <dt>번호</dt>
                  <dd className="short">
                    <TextInput
                      variant="contained"
                      type="text"
                      pattern="\d*"
                      value={inputs.sPhone}
                      id="sPhone"
                      name="sProfile02"
                      onChange={(e) => onChange(e)}
                      maxLength={11}
                      placeholder="'-' 제외 숫자만 작성"
                    />
                  </dd>
                </div>
                <div>
                  <dt>담당</dt>
                  <dd className="short">
                    <TextInput
                      variant="contained"
                      type="text"
                      value={inputs.sChargeName}
                      id="sChargeName"
                      name="sProfile03"
                      onChange={(e) => onChange(e)}
                      maxLength={10}
                    />
                  </dd>
                </div>
                {/* <div>
                  <dt>오픈</dt>
                  <dd className="short">
                    <TextInput variant="contained" type="number" id="sProfile03" name="sProfile03" />
                  </dd>
                </div> */}
              </dl>
            )}

            {/* 임직원 노출 : 임직원이 임직원 프로필을 볼 때만 브랜드 수정 가능 */}
            {userInfo.user_type === "E" && userProfile.user_type === "E" && (
              <dl>
                <div>
                  <dt>브랜드</dt>
                  <dd>
                    <Select
                      size="small"
                      options={brandList}
                      defaultValue={inputs.eBrand}
                      id="eBrand"
                      onChange={(e) => onChange(e)}
                    />
                  </dd>
                </div>
              </dl>
            )}
          </ProfileEditBox>
        )}

        {/* 본사만 노출 : 알림설정 페이지 이동 */}
        {userProfile.user_type === "E" && (
          <StyledLinkBox>
            <Link to="/home-setting">홈화면 설정</Link>
            <Link to="/alarm-setting">알림 설정</Link>
            <Link to="/settings/phrases">자주 쓰는 문구 설정</Link>
            {userInfo.user_id == "m1" && <Link to="/block-user">사용자 차단</Link>}
          </StyledLinkBox>
        )}
        {/* //본사만 노출 */}
      </Body>

      <Modal ref={logoutModalRef} leftBtnText="취소" rightBtnText="확인" onClick={() => cancelLogout()} onClick2={() => confirmLogout()}>
        <p>로그아웃 하시겠습니까?</p>
      </Modal>
    </>
  );
}

const NoMap = styled.div`
  padding-top: calc(60px + ${({ theme }) => theme.mobile.notchHeight}px);
`;

const ImageSliderContainer = styled.div`
  padding-top: calc(60px + ${({ theme }) => theme.mobile.notchHeight}px);
`;

const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  
  > div {
    width: 100% !important;
    height: 100% !important;
  }
`;

const TitleBox = styled.div`
  padding: 30px 15px 0;
  > div {
    padding-bottom: 10px;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    ${({ theme }) => theme.flexBox.flexMixin()}
  }
  .name {
    font-size: 18px;
    font-weight: 700;
    > span {
      padding-left: 8px;
      color: #818181;
      font-family: "Roboto";
      font-size: 14px;
      font-weight: 400;
    }
  }
  .edit-btn {
    border: 0;
    padding: 0 0 0 15px;
    appearance: none;
    background: none;
    color: ${({ theme }) => theme.color.subMain};
    font-size: 14px;
    &.cancel {
      color: ${({ theme }) => theme.color.secondary};
    }
  }
`;

const ProfileBox = styled.div`
  min-height: 104px;
  padding: 0 16px 35px;
  color: ${({ theme }) => theme.color.primary};
  font-size: 14px;
  .ico-info {
    min-height: 24px;
    line-height: 24px;
    padding-left: 32px;
    margin-top: 16px;
    color: ${({ theme }) => theme.color.primary};
    background-position: 0 0;
    background-size: 24px;
    background-repeat: no-repeat;
  }
  .address {
    background-image: url(${IcoMap});
    > button {
      padding-left: 16px;
      color: ${({ theme }) => theme.color.subMain};
      border: none;
      background: none;
      font-family: inherit;
      font-size: inherit;
      cursor: pointer;
    }
  }
  .email {
    background-image: url(${IcoEmail});
  }
  .tel {
    background-image: url(${IcoTel});
    font-family: "Poppins";
    > a {
      padding-left: 16px;
      color: ${({ theme }) => theme.color.subMain};
    }
  }
  .person {
    background-image: url(${IcoUser});
  }
  .category {
    background-image: url(${IcoCate});
  }
  .brand {
    background-image: url(${IcoCate});
  }
  .info {
    background-image: url(${IcoDate});
    font-family: "Poppins";
  }
  .as {
    background-image: url(${IcoAsed});
    > span {
      padding-right: 24px;
      background: url(${IconLinkGrey}) no-repeat right center;
    }
  }
  .notice {
    background-image: url(${IcoNotice});
    > span {
      padding-right: 24px;
      background: url(${IconLinkGrey}) no-repeat right center;
    }
  }
  .sku {
    background-image: url(${IcoSku});
    > span {
      padding-right: 24px;
      background: url(${IconLinkGrey}) no-repeat right center;
    }
  }
`;

const StoreImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ImageHelpText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.tertiary};
  line-height: 1.4;
`;

const ProfileEditBox = styled.div`
  padding: 12px 15px 0;
  dl {
    > div {
      ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
      margin-top: 8px;
    }
    dt {
      min-width: 24px;
      margin-right: 8px;
      font-size: 14px;
      font-weight: 500;
    }
    dd {
      flex-grow: 1;
      &.short {
        flex-grow: 0;
        flex-basis: 160px;
      }
    }
  }
`;

const StyledLinkBox = styled.div`
  margin-top: 80px;
  padding: 5px 15px;
  background-color: #f4f4f4;
  > a {
    display: block;
    padding: 20px 0;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    font-size: 16px;
    font-weight: 700;
    background: url(${IconLink}) no-repeat right center;
    &:last-child {
      border-bottom: none;
    }
  }
`;

export default profile;
